import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Group,
  ManageAccounts,
  SupervisedUserCircle,
  ArrowDropDown,
  Storefront,
  LockReset,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import InstructorFormDialog from '../components/InstructorFormDialog';
import { formatPhoneInput, isValidPhoneNumber } from '../utils/phoneValidation';
import InstructorHandoverFields from '../components/instructor/InstructorHandoverFields';
import BranchSelect from '../components/BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchChip from '../components/BranchChip';
import { actingStudioOf } from '../utils/studioOf';
import { isOwnerRole } from '../utils/roles';

interface StudioUser {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  role: 'manager' | 'instructor' | 'studio_owner' | 'brand_owner';
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  /** Locations they work at (ids, or populated); instructor rows may carry `studio` only. */
  studios?: any[];
  studio?: any;
}

/** Listed (and offered) top-down: owners, then managers, then instructors. */
const ROLE_ORDER: Record<string, number> = { brand_owner: 0, studio_owner: 0, manager: 1, instructor: 2 };

interface Props {
  studioId?: string; // When embedded in StudioDetailPage
  studioOwner?: { name: string; email: string }; // Passed from StudioDetailPage (admin view)
}

const StudioUsersPage: React.FC<Props> = ({ studioId, studioOwner }) => {
  // ── WHICH LOCATIONS DOES THIS PERSON WORK AT ────────────────────────────
  //
  // `StudioUser.studios` is an array and the create endpoint already accepts
  // `studioIds` (validated to one brand). The panel never sent them, so a
  // manager covering two branches needed two logins — and the second is
  // refused, because the email is unique.
  const { isAllLocations, locations, roleByStudio } = useMyLocations();
  // Pin a row write only under "All locations" (see actingStudioOf).
  const rowPin = (row: any) =>
    actingStudioOf(row, {
      isAllLocations,
      myLocationIds: locations.map((l) => String(l._id)),
      roleByStudio,
    });
  const [addBranches, setAddBranches] = useState<string[]>([]);
  const { admin } = useAuth();
  const [users, setUsers] = useState<StudioUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // "Create Staff Member" entry-point menu (choose Manager vs Instructor).
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);

  // Add (Manager) dialog state. Instructors are created via InstructorFormDialog
  // (the same rich dialog used on the Instructors page) so they get the optional
  // "Grant admin panel access" toggle + studio-page profile fields.
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [instructorDialogOpen, setInstructorDialogOpen] = useState(false);
  // Editing an instructor reuses the same rich dialog (resolved full Instructor record).
  const [instructorEditDialogOpen, setInstructorEditDialogOpen] = useState(false);
  const [instructorToEdit, setInstructorToEdit] = useState<any | null>(null);
  const [resolvingInstructor, setResolvingInstructor] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'manager' as 'manager' | 'instructor' | 'studio_owner',
  });
  const [addFormErrors, setAddFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<StudioUser | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    role: 'manager' as 'manager' | 'instructor' | 'studio_owner',
    isActive: true,
    password: '',
  });
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<StudioUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Password reset dialog state
  const [resetUser, setResetUser] = useState<StudioUser | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);

  const isAdminView = admin?.role === 'super_admin' || admin?.role === 'admin';
  // A brand owner also adds, edits and revokes the OWNER of a location
  // (`studio_owner`). Brand owners themselves are only added by SweatSocial.
  const isBrandOwner = admin?.role === 'brand_owner';
  const canManageRow = (role?: string) =>
    role === 'manager' || role === 'instructor' || (isBrandOwner && role === 'studio_owner');
  // Super admins reset any studio's staff; owners reset their own studio's
  // staff (the studio endpoint is owner-only and scoped to their studio).
  const canResetPassword = admin?.role === 'super_admin' || isOwnerRole(admin?.role);

  // Studio context for InstructorFormDialog: the prop (admin viewing a studio's
  // profile) or the logged-in studio owner's own studio (own /staff page).
  const effectiveStudioId = studioId || (admin as any)?.studio?._id || '';

  // Resolve owner info: from prop (admin view) or from current session (studio/manager view)
  const ownerName = studioOwner?.name || (admin as any)?.studio?.studioName || admin?.name || 'Studio Owner';
  const ownerEmail = studioOwner?.email || (admin as any)?.studio?.email || admin?.email || '';
  const ownerEntry = {
    _id: '__owner__',
    name: ownerName,
    email: ownerEmail,
    phone: undefined as string | undefined,
    role: 'owner' as 'manager' | 'instructor',
    isActive: true,
    createdAt: '',
  };

  // A brand with several locations: each person's locations as bubbles under
  // their name. Single-location studios: nothing (every row would say the same).
  const multiLocation = locations.length > 1;
  const locationsOf = (u: StudioUser): string[] =>
    (u.studios?.length ? u.studios : u.studio ? [u.studio] : [])
      .map((x: any) => String(x?._id ?? x))
      .filter((id: string) => locations.some((l) => String(l._id) === id));
  const LocationBubbles: React.FC<{ ids: string[] }> = ({ ids }) =>
    multiLocation && ids.length ? (
      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
        {ids.map((id) => (
          <BranchChip key={id} location={locations.find((l) => String(l._id) === id)} />
        ))}
      </Box>
    ) : null;
  // Owners, then managers, then instructors (newest first within each, the
  // server's order). Single-location studios keep their list as it was.
  const sortedUsers = multiLocation
    ? [...users].sort((a, b) => (ROLE_ORDER[a.role] ?? 3) - (ROLE_ORDER[b.role] ?? 3))
    : users;

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getStudioUsers(studioId);
      // Response may be array directly or wrapped
      const data = Array.isArray(response) ? response : response?.data || response;
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load staff members');
    } finally {
      setIsLoading(false);
    }
  }, [studioId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const validateAddForm = () => {
    const errors: Record<string, string> = {};
    if (!addForm.name.trim()) errors.name = 'Name is required';
    if (!addForm.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email)) errors.email = 'Invalid email';
    if (!addForm.password) errors.password = 'Password is required';
    else if (addForm.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (!addForm.role) errors.role = 'Role is required';
    if (addForm.phone.trim()) {
      if (!isValidPhoneNumber(addForm.phone)) errors.phone = 'Enter a valid 10-digit phone number';
    } else if (addForm.role === 'instructor') {
      errors.phone = 'Phone is required for instructors';
    }
    setAddFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = () => {
    const errors: Record<string, string> = {};
    if (!editForm.name.trim()) errors.name = 'Name is required';
    if (editForm.phone.trim() && !isValidPhoneNumber(editForm.phone)) {
      errors.phone = 'Enter a valid 10-digit phone number';
    }
    if (editForm.password && editForm.password.length < 6) {
      errors.password = 'Minimum 6 characters';
    }
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddSubmit = async () => {
    if (!validateAddForm()) return;
    try {
      setIsSubmitting(true);
      const payload: any = {
        name: addForm.name.trim(),
        email: addForm.email.trim().toLowerCase(),
        password: addForm.password,
        phone: addForm.phone.trim() || undefined,
        role: addForm.role,
      };
      if (isAdminView && studioId) {
        payload.studioId = studioId;
      }
      if (isAllLocations) {
        if (addBranches.length === 0) {
          setAddFormErrors({ submit: 'Choose which location(s) this person works at' });
          setIsSubmitting(false);
          return;
        }
        // First is the home studio; the rest ride in `studioIds`, which the
        // backend validates against the same brand.
        payload.studioId = addBranches[0];
        if (addBranches.length > 1) payload.studioIds = addBranches;
      }
      await apiService.createStudioUser(
        payload,
        isAllLocations ? addBranches[0] : undefined,
      );
      setAddDialogOpen(false);
      setAddForm({ name: '', email: '', password: '', phone: '', role: 'manager' });
      setAddBranches([]);
      setAddFormErrors({});
      await fetchUsers();
    } catch (err: any) {
      setAddFormErrors({ submit: err?.message || 'Failed to create staff member' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openSimpleEdit = (user: StudioUser) => {
    // Rows this account may not manage never reach here (see handleEditOpen).
    if (!canManageRow(user.role)) return;
    setSelectedUser(user);
    setEditForm({ name: user.name, phone: user.phone || '', role: user.role as any, isActive: user.isActive, password: '' });
    setEditFormErrors({});
    setEditDialogOpen(true);
  };

  const handleEditOpen = async (user: StudioUser) => {
    // Owner-level rows are managed from the brand page — except a location
    // owner, which a brand owner manages here.
    if (!canManageRow(user.role)) return;
    // Instructors edit through the same rich InstructorFormDialog as creation.
    // The table row is a thin StudioUser / merged shape, so resolve the full
    // Instructor record first (by id for instructor-collection rows, else by phone).
    if (user.role === 'instructor') {
      try {
        setResolvingInstructor(true);
        const res: any = await apiService.getInstructors(effectiveStudioId);
        const list: any[] = res?.instructors || res?.data || [];
        const fromInstructorCollection = (user as any)._fromInstructorCollection === true;
        const match =
          (fromInstructorCollection ? list.find((i) => i._id === user._id) : null) ||
          (user.phone ? list.find((i) => i.phoneNumber === user.phone) : null) ||
          list.find((i) => i._id === user._id);
        if (match) {
          setInstructorToEdit(match);
          setInstructorEditDialogOpen(true);
          return;
        }
      } catch {
        /* fall through to the simple dialog */
      } finally {
        setResolvingInstructor(false);
      }
    }
    // Managers (and any instructor without a resolvable profile) use the simple dialog.
    openSimpleEdit(user);
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;
    if (!validateEditForm()) return;
    try {
      setIsSubmitting(true);
      await apiService.updateStudioUser(selectedUser._id, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim() || undefined,
        // A location owner's role is not changed here (the form hides it).
        ...(editForm.role !== 'studio_owner' ? { role: editForm.role } : {}),
        isActive: editForm.isActive,
        // Blank = keep the current password.
        ...(canResetPassword && editForm.password ? { password: editForm.password } : {}),
        // Pinned to the person's location — under "All locations" the
        // switcher sends __all__, which the server refuses for writes.
      }, rowPin(selectedUser));
      setEditDialogOpen(false);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err: any) {
      setError(err?.message || 'Failed to update staff member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openResetPassword = (user: StudioUser) => {
    setResetUser(user);
    setResetPassword('');
    setShowResetPassword(false);
    setResetError(null);
    setResetDone(false);
  };

  const closeResetPassword = () => {
    setResetUser(null);
    setResetPassword('');
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetUser) return;
    if (resetPassword.length < 6) {
      setResetError('Minimum 6 characters');
      return;
    }
    try {
      setIsSubmitting(true);
      setResetError(null);
      // Same PATCH as edit; the backend hashes `password` and audit-logs it.
      await apiService.updateStudioUser(resetUser._id, { password: resetPassword }, rowPin(resetUser));
      setResetDone(true);
    } catch (err: any) {
      setResetError(err?.message || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Set only once the BACKEND has refused the delete, never guessed at.
   *
   * Deleting a staff member whose role is `instructor` removes the instructor
   * too, so it goes through the same handover guard the Instructors page hits.
   * This dialog never handled that refusal: the operator saw "Choose who takes
   * them over" as a red error string with no control to do it with, on both
   * /staff and Studio detail → Staff (one component serves both).
   */
  const [deleteWork, setDeleteWork] = useState<{
    sessionCount: number;
    classCount: number;
    eventCount: number;
    wellnessPoolCount: number;
    serviceCount: number;
    bookingCount: number;
    memberCount: number;
    nextSessionAt: string | null;
  } | null>(null);
  const [replacementId, setReplacementId] = useState('');
  const [notifyMembers, setNotifyMembers] = useState(false);
  const [replacementOptions, setReplacementOptions] = useState<any[]>([]);

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeleteWork(null);
    setReplacementId('');
    setNotifyMembers(false);
  };

  const handleDeleteOpen = (user: StudioUser) => {
    if (!canManageRow(user.role)) return;
    setUserToDelete(user);
    setDeleteWork(null);
    setReplacementId('');
    setNotifyMembers(false);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      setIsDeleting(true);
      await apiService.deleteStudioUser(
        userToDelete._id,
        replacementId || undefined,
        notifyMembers,
        rowPin(userToDelete),
      );
      closeDeleteDialog();
      await fetchUsers();
    } catch (err: any) {
      // Read `err.data`: the response interceptor flattens the axios error, so
      // `err.response` does not exist. This is not an error to show — it is the
      // picker's cue, and the dialog stays open to host it.
      const data = err?.data ?? err?.response?.data;
      if (data?.code === 'has_future_assignments') {
        setDeleteWork({
          sessionCount: data.sessionCount ?? 0,
          classCount: data.classCount ?? 0,
          eventCount: data.eventCount ?? 0,
          wellnessPoolCount: data.wellnessPoolCount ?? 0,
          serviceCount: data.serviceCount ?? 0,
          bookingCount: data.bookingCount ?? 0,
          memberCount: data.memberCount ?? 0,
          nextSessionAt: data.nextSessionAt ?? null,
        });
        setError(null);
        // The replacement must be an INSTRUCTOR id, and the staff list holds
        // studio-user ids — they are different entities, so the options are
        // fetched rather than derived from the rows on screen.
        try {
          const res: any = await apiService.getInstructorsByStudio(effectiveStudioId);
          const list: any[] = res?.instructors ?? res?.data ?? [];
          setReplacementOptions(
            list.filter(
              (i) => !i.isDisabled && i.phoneNumber !== userToDelete.phone,
            ),
          );
        } catch {
          setReplacementOptions([]);
        }
      } else {
        setError(data?.message || err?.message || 'Could not revoke access. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // +1 for owner who is always shown first
  const totalUsers = users.length + 1;
  const managerCount = users.filter((u) => u.role === 'manager').length;
  const instructorCount = users.filter((u) => u.role === 'instructor').length;

  const getRoleChip = (role: string) => {
    if (role === 'owner') {
      return (
        <Chip
          label="Owner"
          size="small"
          variant="outlined"
          sx={{ borderColor: '#F9A825', color: '#F57F17', fontWeight: 700 }}
        />
      );
    }
    // Owner-level StudioUsers (a named person, not the studio login).
    if (role === 'studio_owner' || role === 'brand_owner') {
      return (
        <Chip
          label={role === 'brand_owner' ? 'Brand owner' : 'Owner'}
          size="small"
          variant="outlined"
          sx={{ borderColor: '#F9A825', color: '#F57F17', fontWeight: 700 }}
        />
      );
    }
    if (role === 'manager') {
      return <Chip label="Manager" size="small" color="primary" variant="outlined" />;
    }
    return <Chip label="Instructor" size="small" color="secondary" variant="outlined" />;
  };

  /** Rows shown read-only: owner-level ones this account does not manage. */
  const isOwnerRow = (role?: string) => (role === 'studio_owner' || role === 'brand_owner') && !canManageRow(role);

  const getStatusChip = (isActive: boolean) => {
    return isActive ? (
      <Chip label="Active" size="small" color="success" variant="outlined" />
    ) : (
      <Chip label="Inactive" size="small" color="default" variant="outlined" />
    );
  };

  // Manager gets read-only view; only studio owner, admin, super_admin can add/edit/delete
  const canManage = admin?.role !== 'instructor' && admin?.role !== 'manager';

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Group sx={{ fontSize: 36, color: 'text.secondary' }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>{totalUsers}</Typography>
              <Typography variant="body2" color="text.secondary">Total Staff</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ManageAccounts sx={{ fontSize: 36, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight={700} color="primary.main">{managerCount}</Typography>
              <Typography variant="body2" color="text.secondary">Managers</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SupervisedUserCircle sx={{ fontSize: 36, color: 'secondary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight={700} color="secondary.main">{instructorCount}</Typography>
              <Typography variant="body2" color="text.secondary">Instructors</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Table Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          Staff Members
        </Typography>
        {canManage && (
          <>
            <Button
              variant="contained"
              startIcon={<Add />}
              endIcon={<ArrowDropDown />}
              onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            >
              Create Staff Member
            </Button>
            <Menu
              anchorEl={createMenuAnchor}
              open={Boolean(createMenuAnchor)}
              onClose={() => setCreateMenuAnchor(null)}
            >
              {/* Top-down: location owner, manager, instructor. */}
              {isBrandOwner && (
                <MenuItem
                  onClick={() => {
                    setCreateMenuAnchor(null);
                    setAddForm({ name: '', email: '', password: '', phone: '', role: 'studio_owner' });
                    setAddFormErrors({});
                    setAddDialogOpen(true);
                  }}
                >
                  <ListItemIcon><Storefront fontSize="small" /></ListItemIcon>
                  Location owner
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setCreateMenuAnchor(null);
                  setAddForm({ name: '', email: '', password: '', phone: '', role: 'manager' });
                  setAddFormErrors({});
                  setAddDialogOpen(true);
                }}
              >
                <ListItemIcon><ManageAccounts fontSize="small" /></ListItemIcon>
                Manager
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setCreateMenuAnchor(null);
                  setInstructorDialogOpen(true);
                }}
              >
                <ListItemIcon><SupervisedUserCircle fontSize="small" /></ListItemIcon>
                Instructor
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              {canManage && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Owner row — always first, always read-only */}
            <TableRow
              sx={{ backgroundColor: 'rgba(249, 168, 37, 0.05)', '&:hover': { backgroundColor: 'rgba(249, 168, 37, 0.09)' } }}
            >
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={700}>{ownerEntry.name}</Typography>
                </Box>
                <LocationBubbles ids={[String((admin as any)?.studio?._id ?? '')].filter((id) => locations.some((l) => String(l._id) === id))} />
              </TableCell>
              <TableCell>{ownerEntry.email}</TableCell>
              <TableCell>—</TableCell>
              <TableCell>{getRoleChip('owner')}</TableCell>
              <TableCell>{getStatusChip(true)}</TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">—</Typography>
              </TableCell>
              {canManage && <TableCell />}
            </TableRow>

            {/* Regular staff rows */}
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 7 : 6} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">No additional staff members</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>{user.name}</Typography>
                    <LocationBubbles ids={locationsOf(user)} />
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone || '—'}</TableCell>
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell>{getStatusChip(user.isActive)}</TableCell>
                  <TableCell>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    }) : '—'}
                  </TableCell>
                  {canManage && isOwnerRow(user.role) && (
                    <TableCell>
                      <Tooltip title="Managed from the brand page">
                        <Typography variant="caption" color="text.secondary">
                          Read-only
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  {canManage && !isOwnerRow(user.role) && (
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEditOpen(user)}
                              disabled={resolvingInstructor}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* Instructor-collection rows have no login to reset. */}
                        {canResetPassword && !(user as any)._fromInstructorCollection && (
                          <Tooltip title="Reset password">
                            <IconButton size="small" onClick={() => openResetPassword(user)}>
                              <LockReset fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Revoke access">
                          <IconButton size="small" color="error" onClick={() => handleDeleteOpen(user)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{addForm.role === 'studio_owner' ? 'Create Location Owner' : 'Create Manager'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {addFormErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>{addFormErrors.submit}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {addForm.role === 'studio_owner' && (
              <Alert severity="info">
                A location owner runs the location(s) you choose: its bookings, staff, revenue and settings. They do not see your other locations.
              </Alert>
            )}
            {/* Multi-select: one login can cover several branches of the same
                brand, which is what StudioUser.studios[] is for. */}
            {isAllLocations && (
              <BranchSelect
                value={addBranches}
                onChange={setAddBranches}
                multiple
                label="Works at"
                minRole="owner"
              />
            )}
            <TextField
              label="Full Name"
              value={addForm.name}
              onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
              error={!!addFormErrors.name}
              helperText={addFormErrors.name}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              error={!!addFormErrors.email}
              helperText={addFormErrors.email}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={addForm.password}
              onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
              error={!!addFormErrors.password}
              helperText={addFormErrors.password || 'Minimum 6 characters'}
              fullWidth
              required
            />
            <TextField
              label="Phone (optional)"
              placeholder="XXXXXXXXXX"
              value={addForm.phone}
              onChange={(e) => setAddForm({ ...addForm, phone: formatPhoneInput(e.target.value) })}
              fullWidth
              error={!!addFormErrors.phone}
              helperText={addFormErrors.phone}
              inputProps={{ maxLength: 10, inputMode: 'numeric' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddDialogOpen(false); setAddFormErrors({}); }} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={20} /> : addForm.role === 'studio_owner' ? 'Create Location Owner' : 'Create Staff Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Instructor — same rich dialog as the Instructors page (includes the
          optional "Grant admin panel access (login)" toggle + studio-page profile). */}
      {instructorDialogOpen && (
        <InstructorFormDialog
          open={instructorDialogOpen}
          onClose={() => setInstructorDialogOpen(false)}
          onSuccess={() => { setInstructorDialogOpen(false); fetchUsers(); }}
          studioId={effectiveStudioId}
        />
      )}

      {/* Edit Instructor — same rich dialog (profile fields + password reset for any linked login). */}
      {instructorToEdit && (
        <InstructorFormDialog
          open={instructorEditDialogOpen}
          onClose={() => { setInstructorEditDialogOpen(false); setInstructorToEdit(null); }}
          onSuccess={() => { setInstructorEditDialogOpen(false); setInstructorToEdit(null); fetchUsers(); }}
          instructor={instructorToEdit}
          studioId={instructorToEdit.studio?._id || effectiveStudioId}
        />
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Staff Member</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full Name"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              placeholder="XXXXXXXXXX"
              value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: formatPhoneInput(e.target.value) })}
              fullWidth
              error={!!editFormErrors.phone}
              helperText={editFormErrors.phone}
              inputProps={{ maxLength: 10, inputMode: 'numeric' }}
            />
            {editForm.role !== 'studio_owner' && (
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  label="Role"
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'manager' | 'instructor' })}
                >
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="instructor">Instructor</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.isActive ? 'active' : 'inactive'}
                label="Status"
                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === 'active' })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            {canResetPassword && (
              <TextField
                label="New password"
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                error={!!editFormErrors.password}
                helperText={editFormErrors.password || 'Leave blank to keep current'}
                autoComplete="new-password"
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog (super admin, or studio owner for own staff) */}
      <Dialog open={!!resetUser} onClose={closeResetPassword} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {resetUser?.name} · {resetUser?.email}
          </Typography>
          {resetDone ? (
            <Alert severity="success">Password updated</Alert>
          ) : (
            <TextField
              label="New password"
              type={showResetPassword ? 'text' : 'password'}
              value={resetPassword}
              onChange={(e) => { setResetPassword(e.target.value); setResetError(null); }}
              error={!!resetError}
              helperText={resetError || 'Minimum 6 characters'}
              fullWidth
              autoFocus
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setShowResetPassword((v) => !v)}>
                    {showResetPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                ),
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeResetPassword} disabled={isSubmitting}>
            {resetDone ? 'Close' : 'Cancel'}
          </Button>
          {!resetDone && (
            <Button variant="contained" onClick={handleResetPasswordSubmit} disabled={isSubmitting}>
              {isSubmitting ? <CircularProgress size={20} /> : 'Reset'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog. Once the backend refuses because this person
          is still on the schedule, it shows the SAME handover block the
          Instructors page uses — one component, so the two surfaces cannot
          drift into two different dialogs for one decision. */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth={deleteWork ? 'sm' : 'xs'} fullWidth>
        <DialogTitle>
          {deleteWork ? `Revoke ${userToDelete?.name}'s access?` : 'Revoke access'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteWork ? (
              'They lose Partner App access immediately. Past classes keep their name and are not touched.'
            ) : (
              <>
                Revoke <strong>{userToDelete?.name}</strong>'s access at this location? Nothing is deleted: their login keeps working anywhere else they have access, and it is switched off only if this was their last location.
              </>
            )}
          </Typography>

          {deleteWork && (
            <InstructorHandoverFields
              futureWork={deleteWork}
              replacementOptions={replacementOptions}
              replacementId={replacementId}
              onReplacementChange={setReplacementId}
              notifyMembers={notifyMembers}
              onNotifyMembersChange={setNotifyMembers}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isDeleting || (!!deleteWork && !replacementId)}
          >
            {isDeleting ? (
              <CircularProgress size={20} />
            ) : deleteWork ? (
              'Reassign & revoke'
            ) : (
              'Revoke access'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudioUsersPage;
